import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Image as ImageIcon, Search, ChevronDown, RefreshCcw, LayoutGrid, Upload, X } from 'lucide-react';

interface Category {
  id: number;
  name: string;
  image_url?: string | null;
  subCategories?: Category[];
}

const CategoryListPage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  // --- Editing States (Original Logic) ---
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editImage, setEditImage] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch('http://localhost:2026/api/categories');
      const data = await response.json();
      setCategories(data);
    } catch (err) { console.error(err); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  const toggleExpand = (id: number) => {
    setExpandedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // --- Original Handlers ---
  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setEditImage(file);
      setEditImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUpdate = async (id: number) => {
    try {
      const formData = new FormData();
      formData.append('name', editName);
      if (editImage) formData.append('categoryImage', editImage);
      
      const response = await fetch(`http://localhost:2026/api/categories/${id}`, { 
        method: 'PUT', 
        body: formData 
      });
      
      if (response.ok) { 
        setEditingId(null); 
        fetchCategories(); 
      } else {
        alert('Failed to update');
      }
    } catch (error) { alert('Error updating'); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure? This will delete the category and its image.')) return;
    try {
      const response = await fetch(`http://localhost:2026/api/categories/${id}`, { method: 'DELETE' });
      if (response.ok) fetchCategories();
      else alert('Failed to delete');
    } catch (error) { alert('Error deleting'); }
  };

  const filteredCategories = categories.filter(cat => 
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // --- Component for Sub-Category Rows ---
  const CategoryRow = ({ item, level }: { item: Category, level: number }) => {
    const isEditing = editingId === item.id;
    const hasSubs = item.subCategories && item.subCategories.length > 0;

    return (
      <div className="w-full">
        <div className="group flex items-center justify-between p-2 rounded-xl transition-all hover:bg-[#006d5b]/5 cursor-pointer">
          <div className="flex items-center gap-3 flex-1 min-w-0" onClick={() => !isEditing && toggleExpand(item.id)}>
            {isEditing ? (
              <div className="flex items-center gap-2 w-full" onClick={(e) => e.stopPropagation()}>
                <div className="relative w-8 h-8 shrink-0">
                  <img src={editImagePreview || ''} className="w-full h-full object-cover rounded-lg border" alt="preview" />
                  <label className="absolute inset-0 bg-black/40 flex items-center justify-center text-white rounded-lg cursor-pointer">
                    <Upload size={10} /><input type="file" className="hidden" onChange={handleEditFileChange} />
                  </label>
                </div>
                <input 
                  value={editName} 
                  onChange={e => setEditName(e.target.value)} 
                  className="bg-white border border-[#006d5b] rounded px-2 py-1 text-xs font-bold outline-none flex-1"
                  autoFocus
                />
              </div>
            ) : (
              <>
                <div className="w-9 h-9 bg-white rounded-xl border border-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center shadow-sm">
                  {item.image_url ? <img src={item.image_url} className="w-full h-full object-cover" alt={item.name} /> : <ImageIcon size={14} className="text-gray-200"/>}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-gray-700">{item.name}</span>
                  {hasSubs && <span className="text-[9px] text-gray-400 font-bold uppercase">{item.subCategories?.length} Items</span>}
                </div>
                {hasSubs && <ChevronDown size={14} className={`ml-auto text-gray-300 transition-transform ${expandedIds.includes(item.id) ? 'rotate-180' : ''}`} />}
              </>
            )}
          </div>

          <div className="flex gap-1 ml-2">
            {isEditing ? (
              <>
                <button onClick={(e) => { e.stopPropagation(); handleUpdate(item.id); }} className="p-1 bg-[#006d5b] text-white rounded shadow-sm hover:bg-[#005a4b] transition-colors"><Edit size={10}/></button>
                <button onClick={(e) => { e.stopPropagation(); setEditingId(null); }} className="p-1 bg-gray-100 text-gray-400 rounded shadow-sm hover:bg-gray-200 transition-colors"><X size={10}/></button>
              </>
            ) : (
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-all">
                <button onClick={(e) => { e.stopPropagation(); setEditingId(item.id); setEditName(item.name); setEditImagePreview(item.image_url || null); }} className="p-1 text-gray-400 hover:text-[#006d5b] hover:bg-white rounded-lg shadow-sm"><Edit size={12}/></button>
                <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="p-1 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg shadow-sm"><Trash2 size={12}/></button>
              </div>
            )}
          </div>
        </div>
        {expandedIds.includes(item.id) && hasSubs && (
          <div className="ml-6 border-l border-gray-100 pl-2 mt-1 space-y-1">
            {item.subCategories!.map(sub => <CategoryRow key={sub.id} item={sub} level={level + 1} />)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 pb-10">
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between px-2">
        <div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">Category Explorer</h2>
          <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">TradyFrog Management Suite</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#006d5b] transition-colors" size={16} />
            <input 
              type="text" placeholder="Quick search..." 
              className="bg-white border border-gray-100 rounded-xl py-2.5 pl-10 pr-4 text-xs font-bold shadow-sm focus:ring-2 focus:ring-[#006d5b]/10 w-64 outline-none transition-all"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button onClick={fetchCategories} className="p-2.5 bg-white border border-gray-100 rounded-xl shadow-sm text-gray-400 hover:text-[#006d5b] transition-all hover:rotate-180 duration-500"><RefreshCcw size={18} /></button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-2 animate-pulse">
          {[1, 2, 3].map(i => <div key={i} className="h-28 bg-gray-50 rounded-[30px]"></div>)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-2 items-start">
          {filteredCategories.map((mainCat) => (
            <div key={mainCat.id} className="flex flex-col group">
              {/* Main Luxury Card */}
              <div 
                onClick={() => !editingId && toggleExpand(mainCat.id)}
                className={`flex items-center gap-4 p-5 rounded-[30px] border-2 transition-all duration-300 cursor-pointer relative
                  ${expandedIds.includes(mainCat.id) 
                    ? 'bg-white border-[#006d5b] shadow-2xl shadow-[#006d5b]/10 scale-[1.02]' 
                    : 'bg-white border-transparent shadow-sm hover:border-gray-200 hover:shadow-xl'
                  }`}
              >
                {/* Actions for Main Card */}
                <div className="absolute top-3 right-5 flex gap-1 opacity-0 group-hover:opacity-100 transition-all z-10">
                  <button onClick={(e) => { e.stopPropagation(); setEditingId(mainCat.id); setEditName(mainCat.name); setEditImagePreview(mainCat.image_url || null); }} className="p-1.5 text-gray-400 hover:text-[#006d5b] hover:bg-white rounded-lg shadow-sm"><Edit size={12}/></button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(mainCat.id); }} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-white rounded-lg shadow-sm"><Trash2 size={12}/></button>
                </div>

                {editingId === mainCat.id ? (
                  <div className="flex items-center gap-3 w-full" onClick={(e) => e.stopPropagation()}>
                    <div className="relative w-12 h-12 shrink-0">
                        <img src={editImagePreview || ''} className="w-full h-full object-cover rounded-xl border border-[#006d5b]/20" alt="edit preview" />
                        <label className="absolute inset-0 bg-black/40 flex items-center justify-center text-white rounded-xl cursor-pointer">
                          <Upload size={12} /><input type="file" className="hidden" onChange={handleEditFileChange} />
                        </label>
                    </div>
                    <div className="flex-1 space-y-2">
                        <input 
                          value={editName} 
                          onChange={e => setEditName(e.target.value)} 
                          className="w-full bg-gray-50 border border-[#006d5b] rounded-lg px-3 py-1 text-xs font-black outline-none" 
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button onClick={() => handleUpdate(mainCat.id)} className="flex-1 bg-[#006d5b] text-white py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter shadow-md">Save</button>
                          <button onClick={() => setEditingId(null)} className="flex-1 bg-gray-100 text-gray-400 py-1 rounded-lg text-[10px] font-black uppercase tracking-tighter">Cancel</button>
                        </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-gray-50 rounded-2xl overflow-hidden border border-gray-50 flex-shrink-0 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-500">
                      {mainCat.image_url ? <img src={mainCat.image_url} className="w-full h-full object-cover" alt={mainCat.name} /> : <LayoutGrid className="text-gray-100" size={24} />}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight line-clamp-1">{mainCat.name}</h3>
                      <p className="text-[10px] font-black text-[#006d5b] mt-1 tracking-widest uppercase bg-[#006d5b]/5 px-2 py-0.5 rounded-full inline-block">
                        {mainCat.subCategories?.length || 0} Sub-Categories
                      </p>
                    </div>
                    <div className={`transition-transform duration-300 ${expandedIds.includes(mainCat.id) ? 'rotate-180 text-[#006d5b]' : 'text-gray-300'}`}>
                      <ChevronDown size={20} />
                    </div>
                  </>
                )}
              </div>

              {/* Sub-categories Container (No Scrollbar) */}
              {expandedIds.includes(mainCat.id) && mainCat.subCategories && (
                <div className="mx-6 p-4 pt-10 -mt-8 bg-white/40 backdrop-blur-sm border-x border-b border-gray-100 rounded-b-[35px] shadow-sm animate-in fade-in slide-in-from-top-4 duration-300 no-scrollbar overflow-y-auto max-h-[350px]">
                   {mainCat.subCategories.length > 0 ? (
                     <div className="space-y-1">
                        {mainCat.subCategories.map(sub => <CategoryRow key={sub.id} item={sub} level={0} />)}
                     </div>
                   ) : (
                     <p className="text-center text-[10px] font-black text-gray-300 uppercase py-4 tracking-widest">No Child Data</p>
                   )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CategoryListPage;